"use client"

import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { formatNumber } from "@/lib/utils"

export interface InvestorTrendPoint {
  date: string
  individual: number
  foreign: number
  institution: number
}

type SeriesKey = "individual" | "foreign" | "institution"

const SERIES: { key: SeriesKey; name: string; color: string }[] = [
  { key: "individual", name: "개인(백만원)", color: "#f87171" },
  { key: "foreign", name: "외국인(백만원)", color: "#60a5fa" },
  { key: "institution", name: "기관계(백만원)", color: "#34d399" },
]

const MA_WINDOW = 5

// 각 시리즈의 5일 이동평균을 `${key}Ma` 필드로 덧붙인다 (추세를 굵은 선으로 겹쳐 보여주기 위함).
function withMovingAverage(data: InvestorTrendPoint[]) {
  return data.map((row, i) => {
    const window = data.slice(Math.max(0, i - MA_WINDOW + 1), i + 1)
    const ma: Record<string, number> = {}
    for (const { key } of SERIES) {
      ma[`${key}Ma`] = window.reduce((sum, r) => sum + r[key], 0) / window.length
    }
    return { ...row, ...ma }
  })
}

export default function InvestorTrendChart({ data }: { data: InvestorTrendPoint[] }) {
  const chartData = withMovingAverage(data)

  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
        <XAxis dataKey="date" tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#3f3f46" }} />
        <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#3f3f46" }} width={48} tickFormatter={formatNumber} />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null
            const row = payload[0]?.payload as (InvestorTrendPoint & Record<string, number>) | undefined
            if (!row) return null
            return (
              <div style={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 6, padding: "8px 10px", fontSize: 12 }}>
                <div style={{ color: "#e4e4e7", marginBottom: 4 }}>{label}</div>
                {SERIES.map(({ key, name, color }) => (
                  <div key={key} style={{ color }}>
                    {name} {formatNumber(row[key])}{" "}
                    <span style={{ color: "#71717a" }}>(5일평균 {formatNumber(Math.round(row[`${key}Ma`]))})</span>
                  </div>
                ))}
              </div>
            )
          }}
        />
        <Legend
          content={() => (
            <div style={{ display: "flex", justifyContent: "center", gap: 16, fontSize: 12, color: "#a1a1aa", paddingTop: 8 }}>
              {SERIES.map(({ key, name, color }) => (
                <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 12, height: 2, background: color }} />
                  {name}
                </span>
              ))}
            </div>
          )}
        />
        {SERIES.map(({ key, color }) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={color}
            strokeWidth={1}
            strokeOpacity={0.35}
            dot={false}
            isAnimationActive={false}
          />
        ))}
        {SERIES.map(({ key, color }) => (
          <Line key={`${key}Ma`} type="monotone" dataKey={`${key}Ma`} stroke={color} strokeWidth={2.5} dot={false} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  )
}
